import { ActivitySpinnerSize, ActivitySpinnerWidget, RadioButtonWithLabelOption } from '@tableau/widgets';
import { FormikContextType, useFormikContext } from 'formik';
import React from 'react';
import { SessionServerSelectors, SessionSiteSelectors } from '../../../../app/selectors/SessionInfoSelectors';
import { Formatters, Messages } from '../../../../codegen/Localize';
import { FormikRadioButtonGroup } from '../../../../commonInfra/components/FormikRadioButtonGroup';
import { ExtractEncryptionService } from '../../../../extract/services/ExtractEncryptionService';
import { ExtractEncryptionMode } from '../../../../sites/SitePropertyTypes';
import { IGetExtractEncryptionStatsResult } from '../../../../ts/ServerApi/requests/GetExtractEncryptionStatsRequest';
import { IExtractEncryptionStats } from '../../../../ts/ServerApi/types/IExtractEncryptionStats';
import { SettingsSection } from '../../../components/common/SettingsSection';
import { formError, settingsRadioButtonItemHeight } from '../../../styles/CommonStyles';
import { statusContainer } from '../../../styles/ExtractEncryptionSettingStyles';
import { useSelector } from '../../utils';
import { SiteSettingsForSiteAdminProps } from '../site-settings/ReactSiteSettingsForSiteAdmin';
import { componentRegistryEntry } from '../SettingsComponentRegistryFunctions';
import { getSessionManagementServiceInstance } from '../../../../sessionManagement/SessionManagementService';

export const EXTRACT_ENCRYPTION_SECTION_TEST_ID = 'extract-encryption-site-setting';
export const EXTRACT_ENCRYPTION_MODE_DISABLED_BUTTON_TEST_ID = 'encryption-mode-disabled';
export const EXTRACT_ENCRYPTION_MODE_ENABLED_BUTTON_TEST_ID = 'encryption-mode-enabled';
export const EXTRACT_ENCRYPTION_MODE_ENFORCED_BUTTON_TEST_ID = 'encryption-mode-enforced';
export const EXTRACT_ENCRYPTION_WARNING_TEST_ID = 'extract-encryption-warning';
export const EXTRACT_ENCRYPTION_ERROR_TEST_ID = 'extract-encryption-error';
export const EXTRACT_ENCRYPTION_STATS_DESCRIPTION_TEST_ID = 'extract-encryption-stats-description';
export const EXTRACT_ENCRYPTION_SPINNER_TEST_ID = 'extract-encryption-spinner';
export const EXTRACT_ENCRYPTION_STATS_ENCRYPTED_TEST_ID = 'extract-encryption-stats-encrypted';
export const EXTRACT_ENCRYPTION_STATS_UNENCRYPTED_TEST_ID = 'extract-encryption-stats-unencrypted';

export type ExtractEncryptionSectionProps = SiteSettingsForSiteAdminProps<'extractEncryptionMode'>;

export function ExtractEncryptionSection(props: ExtractEncryptionSectionProps): JSX.Element {
  // showEncryptionStatus falls back to true because except for the create site dialog,
  // every place where this section gets rendered does need encryption status shown
  const showEncryptionStatus = props.showEncryptionStatus === undefined ? true : props.showEncryptionStatus;
  let siteId = props.siteId? props.siteId: getSessionManagementServiceInstance().sessionSiteId();
  const isExtractEncryptionEnabled = useSelector(SessionServerSelectors.extractEncryptionEnabledOmniSwitch);
  const formik = useFormikContext<ExtractEncryptionSectionProps>();
  const {encryptionHasError, encryptionStats} = useExtractEncryptionAPI(showEncryptionStatus, isExtractEncryptionEnabled, formik, siteId);
  const buttonList = React.useMemo(() => getButtonList(), []);

  return !isExtractEncryptionEnabled ? null : (
    <SettingsSection
      title={Messages.ear_Title()}
      description={Messages.ear_Details()}
      testId={EXTRACT_ENCRYPTION_SECTION_TEST_ID}
    >
      <FormikRadioButtonGroup
        aria-label={Messages.ear_Title()}
        buttonList={buttonList}
        className={settingsRadioButtonItemHeight}
        name={'settings.extractEncryptionMode'}
      />
      {showEncryptionStatus &&
      <div className={statusContainer}>
        {renderEncryptionStatus(encryptionHasError, encryptionStats, formik)}
      </div>}
    </SettingsSection>
  );
}

function useExtractEncryptionAPI(
  showEncryptionStatus: boolean,
  isExtractEncryptionEnabled: boolean,
  formik: FormikContextType<ExtractEncryptionSectionProps>,
  siteId: string
) {
  const [encryptionHasError, setEncryptionHasError] = React.useState(false);
  const [encryptionStats, setEncryptionStats] = React.useState<IExtractEncryptionStats>(undefined);

  // API call to get extract encryption stats
  React.useEffect(
    () => {
      if (showEncryptionStatus && isExtractEncryptionEnabled) {
        let shouldSetState = true; // will be false when component unmounts to prevent memory leak
        const callbackWithResult = (result: IGetExtractEncryptionStatsResult) => {
          if (result?.counts?.encrypting === undefined) {
            // no data means result is invalid so cancel polling and treat it as error
            ExtractEncryptionService.cancelExtractEncryptionStatsPolling();
            shouldSetState && setEncryptionHasError(true);
          } else {
            shouldSetState && setEncryptionStats(result.counts);
          }
        };
        const callbackWithError = () => {
          shouldSetState && setEncryptionHasError(true);
        };

        ExtractEncryptionService.pollExtractEncryptionStats(siteId, callbackWithResult).then(
          callbackWithResult, // success
          callbackWithError, // error
        );

        return () => {
          ExtractEncryptionService.cancelExtractEncryptionStatsPolling();
          shouldSetState = false;
        };
      }
    },
    // formik dependency allows API to be called again after saving setting changes
    [showEncryptionStatus, isExtractEncryptionEnabled, formik.initialValues.settings.extractEncryptionMode]
  );

  return {encryptionHasError, encryptionStats};
}

function renderEncryptionStatus(encryptionHasError: boolean, encryptionStats: IExtractEncryptionStats, formik: FormikContextType<ExtractEncryptionSectionProps>): JSX.Element {
  if (encryptionHasError) {
    return <div className={formError} data-tb-test-id={EXTRACT_ENCRYPTION_ERROR_TEST_ID}>
      <div>{Messages.ear_Status_Error()}</div>
      <div>{Messages.ear_Status_Error_Retry()}</div>
    </div>;
  }

  return <>
    {renderStats(encryptionStats)}
    {renderWarning(formik)}
  </>;
}

function renderStats(encryptionStats: IExtractEncryptionStats): JSX.Element {
  if (!encryptionStats) {
    return null;
  }

  const showSpinner = encryptionStats.decrypting > 0 || encryptionStats.encrypting > 0;
  const numEncryptedExtracts = encryptionStats.decrypting + encryptionStats.encrypted;
  const numUnencryptedExtracts = encryptionStats.encrypting + encryptionStats.unencrypted;
  const totalExtracts =  numEncryptedExtracts + numUnencryptedExtracts;

  return <div>
    <div data-tb-test-id={EXTRACT_ENCRYPTION_STATS_DESCRIPTION_TEST_ID}>
      {getStatsDescriptionMessage(encryptionStats)}
    </div>
    {showSpinner &&
    <ActivitySpinnerWidget spinnerSize={ActivitySpinnerSize.Small} testId={EXTRACT_ENCRYPTION_SPINNER_TEST_ID} />}
    <div data-tb-test-id={EXTRACT_ENCRYPTION_STATS_ENCRYPTED_TEST_ID}>
      {Messages.ear_Stats_Encrypted_Count_Loc({
        ENCRYPTED_COUNT_LOC: Formatters.formatCount(numEncryptedExtracts),
        TOTAL_COUNT_LOC: Formatters.formatCount(totalExtracts)
      })}
    </div>
    <div data-tb-test-id={EXTRACT_ENCRYPTION_STATS_UNENCRYPTED_TEST_ID}>
      {Messages.ear_Stats_Unencrypted_Count_Loc({
        UNENCRYPTED_COUNT_LOC: Formatters.formatCount(numUnencryptedExtracts),
        TOTAL_COUNT_LOC: Formatters.formatCount(totalExtracts)
      })}
    </div>
  </div>;
}

function renderWarning(formik: FormikContextType<ExtractEncryptionSectionProps>): JSX.Element {
  const currentSetting = formik.values.settings.extractEncryptionMode;
  const previousSetting = formik.initialValues.settings.extractEncryptionMode;
  let warningMessage: string;

  if (currentSetting !== previousSetting) {
    if (currentSetting === ExtractEncryptionMode.DISABLED) {
      warningMessage = Messages.ear_Disable_Warning();
    } else if (currentSetting === ExtractEncryptionMode.ENFORCED) {
      warningMessage = Messages.ear_Enforce_Warning();
    } else if (previousSetting === ExtractEncryptionMode.DISABLED) {
      warningMessage = Messages.ear_Disable_To_Enable_Warning();
    } else if (previousSetting === ExtractEncryptionMode.ENFORCED) {
      warningMessage = Messages.ear_Enforce_To_Enable_Warning();
    }
  }

  return !warningMessage ? null : (
    <div className={formError} data-tb-test-id={EXTRACT_ENCRYPTION_WARNING_TEST_ID}>
      {warningMessage}
    </div>
  );
}

function getButtonList(): RadioButtonWithLabelOption<ExtractEncryptionMode>[] {
  return [
    {
      value: ExtractEncryptionMode.DISABLED,
      label: Messages.ear_Disable(),
      testId: EXTRACT_ENCRYPTION_MODE_DISABLED_BUTTON_TEST_ID
    },
    {
      value: ExtractEncryptionMode.ENABLED,
      label: Messages.ear_Enable(),
      testId: EXTRACT_ENCRYPTION_MODE_ENABLED_BUTTON_TEST_ID
    },
    {
      value: ExtractEncryptionMode.ENFORCED,
      label: Messages.ear_Enforce(),
      testId: EXTRACT_ENCRYPTION_MODE_ENFORCED_BUTTON_TEST_ID
    },
  ];
}

function getStatsDescriptionMessage(encryptionStats: IExtractEncryptionStats): string {
  if (encryptionStats.encrypting > 0 && encryptionStats.decrypting > 0) {
    return Messages.ear_Status_Encryption_Decryption_In_Progress();
  } else if (encryptionStats.encrypting > 0) {
    return Messages.ear_Status_Encryption_In_Progress();
  } else if (encryptionStats.decrypting > 0) {
    return Messages.ear_Status_Decryption_In_Progress();
  } else {
    return Messages.ear_Status_Encryption_Not_In_Progress();
  }
}

/* for registering the component in settings registry */
export const ExtractEncryptionSectionSpecs = componentRegistryEntry({
  name: 'ExtractEncryptionSection',
  component: ExtractEncryptionSection
});
